import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, LogIn, Loader2, KeyRound, ShieldCheck, Smartphone, Eye, EyeOff } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";

export default function MemberLogin() {
  const [memberId, setMemberId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [requestingSetup, setRequestingSetup] = useState(false);
  const { loginMember, refresh, isPreview, user } = useAuth();
  const nav = useNavigate();

  // In preview mode, redirect to the admin password gate instead
  useEffect(() => {
    if (isPreview) {
      if (user && (user.role === "admin" || user.role === "primary_admin")) {
        nav("/a", { replace: true });
      } else {
        nav("/admin/login", { replace: true });
      }
    }
  }, [isPreview, user, nav]);

  // Change-password dialog state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // iOS-only PWA install prompt (iOS needs PWA for push notifications)
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    // Already installed as standalone — hide prompt
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) {
      setShowInstall(false);
      return;
    }

    // Detect iOS Safari (only platform that needs PWA install for push)
    const ua = navigator.userAgent || "";
    const iosDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS|Chrome/.test(ua);

    if (iosDevice && isSafari) {
      setShowInstall(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberId.trim()) return;
    setLoading(true);
    try {
      const data = await loginMember(memberId.trim(), password);
      if (data.must_change_password) {
        // Show change-password dialog — don't navigate yet
        setShowChangePassword(true);
        toast.info("Please set your own password before continuing.");
      } else {
        nav("/m");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "password_not_set") {
        setNeedsPassword("setup");
        toast.info("You need to set a password first.");
      } else if (err?.response?.status === 401 && !password) {
        setNeedsPassword(true);
      } else {
        toast.error(detail || "Login failed. Check your Member ID and password.");
      }
    }
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", { new_password: newPassword });
      toast.success("Password set successfully! Welcome to the members portal.");
      setShowChangePassword(false);
      // Now set user session and navigate
      await refresh();
      nav("/m");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to change password. Please try again.");
    }
    setChangingPassword(false);
  };

  const handleRequestSetupLink = async () => {
    if (!memberId.trim()) {
      toast.error("Enter your Member ID first");
      return;
    }
    setRequestingSetup(true);
    try {
      await api.post("/auth/request-password-setup", { member_id: memberId.trim() });
      toast.success("If this Member ID exists, a password setup link has been sent to your device.");
    } catch (err) {
      toast.error("Could not send setup link. Please contact your admin.");
    }
    setRequestingSetup(false);
  };

  return (
    <PublicLayout>
      <div className="flex-1 flex flex-col items-center justify-center py-16 px-4 gap-4">

        {/* iOS PWA Install Prompt — required for push notifications on iPhone/iPad */}
        {showInstall && (
          <Card
            className="w-full max-w-sm p-4 rounded-2xl border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/[0.04]"
            data-testid="pwa-install-card"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center shrink-0 mt-0.5">
                <Smartphone className="h-5 w-5 text-[hsl(var(--accent))]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold tracking-tight">Get the Derwent CC App</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Add to your home screen for quick access and notifications:
                </p>
                <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-none pl-0">
                  <li className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[10px] font-bold shrink-0">1</span>
                    <span>Tap the <strong className="text-foreground">Share</strong> button
                      <svg viewBox="0 0 24 24" className="inline h-4 w-4 ml-0.5 -mt-0.5 text-[hsl(var(--accent))]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                      at the bottom of Safari
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[10px] font-bold shrink-0">2</span>
                    <span>Scroll down and tap <strong className="text-foreground">Add to Home Screen</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[10px] font-bold shrink-0">3</span>
                    <span>Tap <strong className="text-foreground">Add</strong> — the DCC app will appear on your home screen</span>
                  </li>
                </ol>
              </div>
            </div>
          </Card>
        )}

        <Card className="w-full max-w-sm p-6 sm:p-8 rounded-2xl space-y-6" data-testid="member-login-card">
          <div className="text-center space-y-1">
            <Lock className="h-8 w-8 mx-auto text-[hsl(var(--accent))]" />
            <h1 className="text-xl font-bold tracking-tight">Members Portal</h1>
            <p className="text-sm text-muted-foreground">Enter your Member ID and password</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member-id">Member ID</Label>
              <Input
                id="member-id"
                placeholder="0001"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value.toUpperCase())}
                autoFocus
                data-testid="login-member-id"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              disabled={loading || !memberId.trim()}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging in…</>
              ) : (
                <><LogIn className="h-4 w-4 mr-2" /> Enter members portal</>
              )}
            </Button>
          </form>

          {/* Password setup prompt */}
          {needsPassword === "setup" && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 space-y-3" data-testid="password-setup-prompt">
              <p className="text-sm font-medium">Password not set yet</p>
              <p className="text-xs text-muted-foreground">
                You haven't created a password yet. Click below to receive a password setup link on your device.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleRequestSetupLink}
                disabled={requestingSetup}
                className="w-full rounded-full"
                data-testid="request-setup-link-btn"
              >
                {requestingSetup ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                ) : (
                  <><KeyRound className="h-3.5 w-3.5 mr-1.5" /> Send me a setup link</>
                )}
              </Button>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground">
            Don't have an account?{" "}
            <a href="/join" className="text-[hsl(var(--accent))] hover:underline">Apply to join</a>
          </p>
        </Card>
      </div>

      {/* Change Password Dialog — shown after first login with one-time password */}
      <Dialog open={showChangePassword} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md rounded-2xl" onPointerDownOutside={(e) => e.preventDefault()} data-testid="change-password-dialog">
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mb-2">
              <ShieldCheck className="h-6 w-6 text-[hsl(var(--accent))]" />
            </div>
            <DialogTitle className="text-center">Set Your New Password</DialogTitle>
            <DialogDescription className="text-center">
              Your one-time password was used to log in. Please create your own password to continue.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleChangePassword} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  data-testid="toggle-new-password-visibility"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Min 6 characters — can include letters, numbers, and special characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  data-testid="toggle-confirm-password-visibility"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-[11px] text-red-500">Passwords don't match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              disabled={changingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 6}
              data-testid="change-password-submit-btn"
            >
              {changingPassword ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting password…</>
              ) : (
                <><ShieldCheck className="h-4 w-4 mr-2" /> Set Password & Continue</>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
